// src/data/benchmarksBTP.ts

// =============================================================================
// TYPES
// =============================================================================

export interface PlageValeurs {
  min: number;
  max: number;
  moyenne: number;
}

export interface MargesParType {
  gros_oeuvre: PlageValeurs;
  plomberie: PlageValeurs;
  electricite: PlageValeurs;
  peinture: PlageValeurs;
  carrelage: PlageValeurs;
  menuiserie: PlageValeurs;
  chauffage_clim: PlageValeurs;
  renovation_complete: PlageValeurs;
  construction_neuve: PlageValeurs;
  piscine: PlageValeurs;
  facade_ravalement: PlageValeurs;
  toiture_couverture: PlageValeurs;
  terrassement: PlageValeurs;
  isolation: PlageValeurs;
  salle_de_bain: PlageValeurs;
  cuisine: PlageValeurs;
  extension: PlageValeurs;
  amenagement_exterieur: PlageValeurs;
}

export interface CoutsHoraires {
  manoeuvre: PlageValeurs;
  ouvrier_n1: PlageValeurs;
  ouvrier_n2: PlageValeurs;
  ouvrier_n3: PlageValeurs;
  ouvrier_n4: PlageValeurs;
  chef_equipe: PlageValeurs;
  conducteur_travaux: PlageValeurs;
}

export interface RatiosStandards {
  materiaux_ca: PlageValeurs;        // % du CA
  main_oeuvre_ca: PlageValeurs;      // % du CA
  frais_generaux: PlageValeurs;      // % du CA
  charges_patronales: PlageValeurs;  // % du salaire brut
  charges_salariales: PlageValeurs;  // % du salaire brut
}

export interface DelaisMoyens {
  salle_de_bain_standard: PlageValeurs;      // jours
  salle_de_bain_complete: PlageValeurs;
  cuisine_standard: PlageValeurs;
  cuisine_complete: PlageValeurs;
  renovation_appartement_50m2: PlageValeurs;
  renovation_appartement_80m2: PlageValeurs;
  renovation_maison_100m2: PlageValeurs;
  renovation_maison_150m2: PlageValeurs;
  construction_maison_100m2: PlageValeurs;
  piscine_enterree: PlageValeurs;
  facade_maison: PlageValeurs;
  toiture_maison: PlageValeurs;
  extension_30m2: PlageValeurs;
  terrasse_20m2: PlageValeurs;
}

export interface KPIsReference {
  taux_retard_chantiers: number;           // %
  retard_moyen_jours: number;
  taux_depassement_budget: number;         // %
  depassement_moyen_pct: number;           // %
  taux_litiges: number;                    // %
  taux_satisfaction_client: number;        // %
  ratio_heures_facturables_optimal: number; // %
  taux_marge_nette_sain: PlageValeurs;     // %
  ca_par_salarie_annuel: PlageValeurs;     // €
}

export interface RegionBenchmark {
  nom: string;
  code_region: string;
  coefficient_prix: number;      // Multiplicateur vs national
  coefficient_salaires: number;  // Multiplicateur vs national
  coefficient_materiaux: number; // Multiplicateur vs national
  specificites: string[];
  marges_ajustees: PlageValeurs;
  tension_main_oeuvre: 'faible' | 'moyenne' | 'forte' | 'tres_forte';
  saisonnalite: 'faible' | 'moyenne' | 'forte';
  types_travaux_dominants: string[];
}

// =============================================================================
// BENCHMARKS NATIONAUX (France métropolitaine 2024-2025)
// Sources : FFB, CAPEB, INSEE, Observatoire des métiers BTP
// =============================================================================

export const BENCHMARKS_NATIONAUX = {
  // -------------------------------------------------------------------------
  // MARGES PAR TYPE DE TRAVAUX (% du CA HT)
  // -------------------------------------------------------------------------
  marges: {
    // Gros œuvre
    gros_oeuvre: { min: 15, max: 25, moyenne: 20 },
    construction_neuve: { min: 12, max: 20, moyenne: 16 },
    terrassement: { min: 15, max: 25, moyenne: 20 },
    extension: { min: 18, max: 28, moyenne: 23 },

    // Second œuvre
    plomberie: { min: 25, max: 38, moyenne: 31 },
    electricite: { min: 25, max: 38, moyenne: 31 },
    chauffage_clim: { min: 22, max: 32, moyenne: 27 },
    isolation: { min: 20, max: 30, moyenne: 25 },
    menuiserie: { min: 22, max: 32, moyenne: 27 },

    // Finitions
    peinture: { min: 30, max: 45, moyenne: 37 },
    carrelage: { min: 25, max: 38, moyenne: 31 },
    facade_ravalement: { min: 25, max: 38, moyenne: 31 },

    // Pièces complètes
    salle_de_bain: { min: 25, max: 35, moyenne: 30 },
    cuisine: { min: 22, max: 32, moyenne: 27 },
    renovation_complete: { min: 20, max: 30, moyenne: 25 },

    // Extérieur
    piscine: { min: 25, max: 40, moyenne: 32 },
    toiture_couverture: { min: 20, max: 30, moyenne: 25 },
    amenagement_exterieur: { min: 25, max: 38, moyenne: 31 },
  } as MargesParType,

  // -------------------------------------------------------------------------
  // COÛTS HORAIRES MAIN D'ŒUVRE (€/h, charges patronales incluses)
  // Basé sur grille CAPEB/FFB 2024
  // -------------------------------------------------------------------------
  couts_horaires: {
    manoeuvre: { min: 22, max: 28, moyenne: 25 },
    ouvrier_n1: { min: 26, max: 32, moyenne: 29 },        // Ouvrier d'exécution
    ouvrier_n2: { min: 30, max: 38, moyenne: 34 },        // Ouvrier professionnel
    ouvrier_n3: { min: 36, max: 46, moyenne: 41 },        // Compagnon professionnel
    ouvrier_n4: { min: 42, max: 52, moyenne: 47 },        // Maître ouvrier
    chef_equipe: { min: 48, max: 60, moyenne: 54 },
    conducteur_travaux: { min: 58, max: 75, moyenne: 66 },
  } as CoutsHoraires,

  // -------------------------------------------------------------------------
  // RATIOS STANDARDS (% du CA ou du salaire)
  // -------------------------------------------------------------------------
  ratios: {
    materiaux_ca: { min: 25, max: 40, moyenne: 32 },      // Part matériaux dans CA
    main_oeuvre_ca: { min: 35, max: 50, moyenne: 42 },    // Part MO dans CA
    frais_generaux: { min: 8, max: 15, moyenne: 11 },     // Frais de structure
    charges_patronales: { min: 42, max: 47, moyenne: 45 },
    charges_salariales: { min: 20, max: 23, moyenne: 22 },
  } as RatiosStandards,

  // -------------------------------------------------------------------------
  // DÉLAIS MOYENS PAR TYPE DE TRAVAUX (jours ouvrés)
  // -------------------------------------------------------------------------
  delais: {
    // Pièces
    salle_de_bain_standard: { min: 5, max: 10, moyenne: 7 },
    salle_de_bain_complete: { min: 10, max: 20, moyenne: 14 },
    cuisine_standard: { min: 4, max: 8, moyenne: 6 },
    cuisine_complete: { min: 8, max: 15, moyenne: 11 },

    // Rénovation
    renovation_appartement_50m2: { min: 20, max: 40, moyenne: 28 },
    renovation_appartement_80m2: { min: 30, max: 55, moyenne: 42 },
    renovation_maison_100m2: { min: 45, max: 90, moyenne: 65 },
    renovation_maison_150m2: { min: 60, max: 120, moyenne: 85 },

    // Construction
    construction_maison_100m2: { min: 180, max: 300, moyenne: 240 },

    // Extérieur
    piscine_enterree: { min: 30, max: 60, moyenne: 45 },
    facade_maison: { min: 8, max: 18, moyenne: 12 },
    toiture_maison: { min: 5, max: 12, moyenne: 8 },
    extension_30m2: { min: 40, max: 80, moyenne: 55 },
    terrasse_20m2: { min: 5, max: 12, moyenne: 8 },
  } as DelaisMoyens,

  // -------------------------------------------------------------------------
  // KPIs DE RÉFÉRENCE SECTEUR BTP
  // -------------------------------------------------------------------------
  kpis: {
    taux_retard_chantiers: 35,              // 35% des chantiers ont du retard
    retard_moyen_jours: 4,                  // Retard moyen quand il y a retard
    taux_depassement_budget: 28,            // 28% dépassent le budget
    depassement_moyen_pct: 12,              // Dépassement moyen de 12%
    taux_litiges: 8,                        // 8% des chantiers ont un litige
    taux_satisfaction_client: 82,           // Satisfaction moyenne
    ratio_heures_facturables_optimal: 80,   // Objectif : 80% minimum
    taux_marge_nette_sain: { min: 3, max: 8, moyenne: 5 },
    ca_par_salarie_annuel: { min: 80000, max: 150000, moyenne: 110000 },
  } as KPIsReference,

  // -------------------------------------------------------------------------
  // SEUILS D'ALERTE
  // -------------------------------------------------------------------------
  seuils_alerte: {
    marge_critique: 10,            // Marge < 10% = critique
    marge_attention: 18,           // Marge < 18% = attention
    marge_bonne: 25,               // Marge >= 25% = bonne
    marge_excellente: 35,          // Marge >= 35% = excellente

    retard_acceptable_jours: 2,    // Jusqu'à 2 jours = acceptable
    retard_attention_jours: 5,     // 3-5 jours = attention
    retard_critique_jours: 10,     // > 10 jours = critique

    depassement_acceptable_pct: 5,
    depassement_attention_pct: 15,
    depassement_critique_pct: 25,
  },
};

// =============================================================================
// BENCHMARKS PAR RÉGION (13 régions métropolitaines)
// Coefficients basés sur indices FFB régionaux 2024
// =============================================================================

export const BENCHMARKS_REGIONS: Record<string, RegionBenchmark> = {
  'Île-de-France': {
    nom: 'Île-de-France',
    code_region: '11',
    coefficient_prix: 1.18,
    coefficient_salaires: 1.15,
    coefficient_materiaux: 1.08,
    specificites: [
      'Coûts les plus élevés de France',
      'Forte tension sur la main d\'œuvre',
      'Délais rallongés (autorisations)',
      'Forte demande rénovation énergétique',
      'Marchés copropriétés importants',
    ],
    marges_ajustees: { min: 18, max: 28, moyenne: 23 },
    tension_main_oeuvre: 'tres_forte',
    saisonnalite: 'faible',
    types_travaux_dominants: ['Rénovation appartement', 'Copropriété', 'Bureaux'],
  },

  'Auvergne-Rhône-Alpes': {
    nom: 'Auvergne-Rhône-Alpes',
    code_region: '84',
    coefficient_prix: 1.08,
    coefficient_salaires: 1.06,
    coefficient_materiaux: 1.03,
    specificites: [
      'Marché dynamique (Lyon, Grenoble)',
      'Forte demande construction neuve',
      'Rénovation thermique montagne',
      'Saisonnalité stations de ski',
    ],
    marges_ajustees: { min: 20, max: 30, moyenne: 25 },
    tension_main_oeuvre: 'forte',
    saisonnalite: 'moyenne',
    types_travaux_dominants: ['Construction neuve', 'Rénovation thermique', 'Chalets'],
  },

  'Provence-Alpes-Côte d\'Azur': {
    nom: 'Provence-Alpes-Côte d\'Azur',
    code_region: '93',
    coefficient_prix: 1.12,
    coefficient_salaires: 1.08,
    coefficient_materiaux: 1.05,
    specificites: [
      'Forte saisonnalité (été)',
      'Clientèle haut de gamme sur la côte',
      'Piscines très demandées',
      'Climatisation quasi-systématique',
      'Rénovation mas provençaux',
    ],
    marges_ajustees: { min: 22, max: 35, moyenne: 28 },
    tension_main_oeuvre: 'forte',
    saisonnalite: 'forte',
    types_travaux_dominants: ['Piscine', 'Climatisation', 'Rénovation luxe', 'Terrasses'],
  },

  'Nouvelle-Aquitaine': {
    nom: 'Nouvelle-Aquitaine',
    code_region: '75',
    coefficient_prix: 1.02,
    coefficient_salaires: 1.00,
    coefficient_materiaux: 1.00,
    specificites: [
      'Marché équilibré',
      'Forte activité Bordeaux métropole',
      'Rénovation patrimoine ancien',
      'Développement côte atlantique',
    ],
    marges_ajustees: { min: 20, max: 30, moyenne: 25 },
    tension_main_oeuvre: 'moyenne',
    saisonnalite: 'moyenne',
    types_travaux_dominants: ['Rénovation', 'Extension', 'Piscine'],
  },

  'Occitanie': {
    nom: 'Occitanie',
    code_region: '76',
    coefficient_prix: 1.03,
    coefficient_salaires: 1.01,
    coefficient_materiaux: 1.00,
    specificites: [
      'Croissance démographique forte',
      'Construction neuve dynamique (Toulouse, Montpellier)',
      'Climatisation en hausse',
      'Rénovation centres historiques',
    ],
    marges_ajustees: { min: 20, max: 30, moyenne: 25 },
    tension_main_oeuvre: 'forte',
    saisonnalite: 'moyenne',
    types_travaux_dominants: ['Construction neuve', 'Climatisation', 'Rénovation'],
  },

  'Hauts-de-France': {
    nom: 'Hauts-de-France',
    code_region: '32',
    coefficient_prix: 0.95,
    coefficient_salaires: 0.94,
    coefficient_materiaux: 0.97,
    specificites: [
      'Coûts inférieurs à la moyenne',
      'Forte demande isolation/chauffage',
      'Rénovation patrimoine industriel',
      'Proximité Belgique (matériaux)',
    ],
    marges_ajustees: { min: 18, max: 28, moyenne: 23 },
    tension_main_oeuvre: 'moyenne',
    saisonnalite: 'faible',
    types_travaux_dominants: ['Isolation', 'Chauffage', 'Rénovation'],
  },

  'Grand Est': {
    nom: 'Grand Est',
    code_region: '44',
    coefficient_prix: 0.97,
    coefficient_salaires: 0.96,
    coefficient_materiaux: 0.98,
    specificites: [
      'Proximité Allemagne/Luxembourg (concurrence)',
      'Forte demande efficacité énergétique',
      'Climat rigoureux (isolation)',
      'Rénovation centres-villes',
    ],
    marges_ajustees: { min: 18, max: 28, moyenne: 23 },
    tension_main_oeuvre: 'moyenne',
    saisonnalite: 'moyenne',
    types_travaux_dominants: ['Isolation', 'Chauffage', 'Menuiserie'],
  },

  'Pays de la Loire': {
    nom: 'Pays de la Loire',
    code_region: '52',
    coefficient_prix: 1.00,
    coefficient_salaires: 0.99,
    coefficient_materiaux: 0.98,
    specificites: [
      'Marché très dynamique (Nantes)',
      'Construction neuve soutenue',
      'Littoral = rénovation + résidences secondaires',
    ],
    marges_ajustees: { min: 20, max: 30, moyenne: 25 },
    tension_main_oeuvre: 'forte',
    saisonnalite: 'moyenne',
    types_travaux_dominants: ['Construction neuve', 'Extension', 'Rénovation'],
  },

  'Bretagne': {
    nom: 'Bretagne',
    code_region: '53',
    coefficient_prix: 0.98,
    coefficient_salaires: 0.97,
    coefficient_materiaux: 0.97,
    specificites: [
      'Climat humide = étanchéité importante',
      'Rénovation maisons en pierre',
      'Littoral = traitement anti-sel',
      'Bonne dynamique construction',
    ],
    marges_ajustees: { min: 20, max: 30, moyenne: 25 },
    tension_main_oeuvre: 'moyenne',
    saisonnalite: 'moyenne',
    types_travaux_dominants: ['Étanchéité', 'Toiture', 'Rénovation pierre'],
  },

  'Normandie': {
    nom: 'Normandie',
    code_region: '28',
    coefficient_prix: 0.98,
    coefficient_salaires: 0.97,
    coefficient_materiaux: 0.97,
    specificites: [
      'Rénovation patrimoine normand',
      'Proximité Paris (résidences secondaires)',
      'Climat humide = étanchéité',
      'Marché stable et équilibré',
    ],
    marges_ajustees: { min: 20, max: 30, moyenne: 25 },
    tension_main_oeuvre: 'moyenne',
    saisonnalite: 'faible',
    types_travaux_dominants: ['Rénovation', 'Toiture', 'Façade'],
  },

  'Bourgogne-Franche-Comté': {
    nom: 'Bourgogne-Franche-Comté',
    code_region: '27',
    coefficient_prix: 0.94,
    coefficient_salaires: 0.93,
    coefficient_materiaux: 0.95,
    specificites: [
      'Coûts parmi les plus bas',
      'Rénovation patrimoine viticole',
      'Climat continental = isolation',
      'Main d\'œuvre moins tendue',
    ],
    marges_ajustees: { min: 20, max: 32, moyenne: 26 },
    tension_main_oeuvre: 'faible',
    saisonnalite: 'faible',
    types_travaux_dominants: ['Rénovation', 'Isolation', 'Chauffage'],
  },

  'Centre-Val de Loire': {
    nom: 'Centre-Val de Loire',
    code_region: '24',
    coefficient_prix: 0.95,
    coefficient_salaires: 0.94,
    coefficient_materiaux: 0.96,
    specificites: [
      'Rénovation châteaux et patrimoine',
      'Proximité Paris = flux résidentiels',
      'Coûts modérés',
      'Agriculture = bâtiments agricoles',
    ],
    marges_ajustees: { min: 20, max: 30, moyenne: 25 },
    tension_main_oeuvre: 'faible',
    saisonnalite: 'faible',
    types_travaux_dominants: ['Rénovation patrimoine', 'Extension', 'Agricole'],
  },

  'Corse': {
    nom: 'Corse',
    code_region: '94',
    coefficient_prix: 1.20,
    coefficient_salaires: 1.10,
    coefficient_materiaux: 1.25,
    specificites: [
      'Coûts matériaux très élevés (transport)',
      'Forte saisonnalité touristique',
      'Réglementation spécifique',
      'Main d\'œuvre limitée',
      'Clientèle résidences secondaires',
    ],
    marges_ajustees: { min: 25, max: 40, moyenne: 32 },
    tension_main_oeuvre: 'tres_forte',
    saisonnalite: 'forte',
    types_travaux_dominants: ['Piscine', 'Rénovation', 'Climatisation'],
  },
};

// =============================================================================
// FONCTIONS UTILITAIRES
// =============================================================================

/**
 * Récupère les benchmarks ajustés pour une région
 */
export function getBenchmarksRegion(region: string) {
  const regionData = BENCHMARKS_REGIONS[region];
  if (!regionData) {
    return { ...BENCHMARKS_NATIONAUX, region: null };
  }

  // Ajuster les coûts horaires selon le coefficient régional
  const coutsAjustes = Object.fromEntries(
    Object.entries(BENCHMARKS_NATIONAUX.couts_horaires).map(([key, val]) => [
      key,
      {
        min: Math.round(val.min * regionData.coefficient_salaires),
        max: Math.round(val.max * regionData.coefficient_salaires),
        moyenne: Math.round(val.moyenne * regionData.coefficient_salaires),
      },
    ])
  );

  return {
    ...BENCHMARKS_NATIONAUX,
    couts_horaires: coutsAjustes,
    marges: {
      ...BENCHMARKS_NATIONAUX.marges,
      // Override avec marges régionales si significativement différentes
    },
    region: regionData,
  };
}

/**
 * Évalue la performance d'un chantier par rapport aux benchmarks
 */
export function evaluerPerformance(
  margeReelle: number,
  typeChantier: keyof MargesParType,
  region?: string
): 'critique' | 'attention' | 'normale' | 'bonne' | 'excellente' {
  const benchmarks = region
    ? getBenchmarksRegion(region)
    : BENCHMARKS_NATIONAUX;

  const margeBenchmark = benchmarks.marges[typeChantier];
  const seuils = BENCHMARKS_NATIONAUX.seuils_alerte;

  if (margeReelle < seuils.marge_critique) return 'critique';
  if (margeReelle < seuils.marge_attention) return 'attention';
  if (margeReelle < margeBenchmark.moyenne) return 'normale';
  if (margeReelle < seuils.marge_excellente) return 'bonne';
  return 'excellente';
}

/**
 * Calcule le score global d'un chantier (0-100)
 */
export function calculerScoreChantier(params: {
  margeReelle: number;
  margePrevue: number;
  joursReels: number;
  joursPrevus: number;
  typeChantier: keyof MargesParType;
  region?: string;
}): number {
  const { margeReelle, margePrevue, joursReels, joursPrevus, typeChantier, region } = params;

  const benchmarks = region ? getBenchmarksRegion(region) : BENCHMARKS_NATIONAUX;
  const margeBenchmark = benchmarks.marges[typeChantier];

  let score = 50; // Score de base

  // Score marge (40 points max)
  const ecartMarge = margeReelle - margeBenchmark.moyenne;
  score += Math.min(20, Math.max(-20, ecartMarge * 2));

  // Bonus si marge > prévue
  if (margeReelle > margePrevue) {
    score += Math.min(10, (margeReelle - margePrevue) * 0.5);
  }

  // Score délais (30 points max)
  const ecartJours = joursPrevus - joursReels;
  if (ecartJours >= 0) {
    // En avance ou à l'heure
    score += Math.min(15, ecartJours * 2);
  } else {
    // En retard
    score += Math.max(-15, ecartJours * 1.5);
  }

  // Score respect prévisions (20 points max)
  const precisionMarge = 100 - Math.abs((margeReelle - margePrevue) / margePrevue * 100);
  score += Math.min(10, precisionMarge / 10);

  return Math.round(Math.min(100, Math.max(0, score)));
}

/**
 * Génère un verdict textuel basé sur le score
 */
export function genererVerdict(score: number): {
  label: string;
  emoji: string;
  couleur: string;
} {
  if (score >= 90) return { label: 'Exceptionnel', emoji: '🏆', couleur: '#22c55e' };
  if (score >= 80) return { label: 'Excellent', emoji: '⭐', couleur: '#22c55e' };
  if (score >= 70) return { label: 'Très bien', emoji: '✅', couleur: '#84cc16' };
  if (score >= 60) return { label: 'Bien', emoji: '👍', couleur: '#eab308' };
  if (score >= 50) return { label: 'Correct', emoji: '➖', couleur: '#f97316' };
  if (score >= 40) return { label: 'À améliorer', emoji: '⚠️', couleur: '#f97316' };
  return { label: 'Critique', emoji: '🚨', couleur: '#ef4444' };
}

// =============================================================================
// MAPPING TYPE CHANTIER -> CATÉGORIE BENCHMARK
// =============================================================================

export const MAPPING_PICTOGRAMME_TYPE: Record<string, keyof MargesParType> = {
  // Gros œuvre
  'Construction': 'construction_neuve',
  'BrickWall': 'gros_oeuvre',
  'Home': 'renovation_complete',
  'Building2': 'construction_neuve',
  'Factory': 'gros_oeuvre',
  'ArrowUpFromLine': 'extension',
  'Mountain': 'terrassement',
  'HardHat': 'gros_oeuvre',

  // Second œuvre
  'Zap': 'electricite',
  'Wrench': 'plomberie',
  'Flame': 'chauffage_clim',
  'Snowflake': 'chauffage_clim',
  'Wind': 'chauffage_clim',
  'ShowerHead': 'salle_de_bain',
  'CookingPot': 'cuisine',
  'DoorOpen': 'menuiserie',
  'DoorClosed': 'menuiserie',
  'Layers': 'isolation',
  'Shield': 'gros_oeuvre',
  'Package': 'gros_oeuvre',

  // Finitions
  'Paintbrush': 'peinture',
  'Brush': 'facade_ravalement',
  'Grid3x3': 'carrelage',
  'TreeDeciduous': 'menuiserie',
  'Sparkles': 'carrelage',
  'Square': 'menuiserie',
  'Image': 'peinture',
  'Lightbulb': 'electricite',
  'Droplets': 'plomberie',
  'Sofa': 'renovation_complete',

  // Extérieur
  'Waves': 'piscine',
  'TreePine': 'amenagement_exterieur',
  'Leaf': 'amenagement_exterieur',
  'Fence': 'amenagement_exterieur',
  'Car': 'gros_oeuvre',
  'Hexagon': 'amenagement_exterieur',
  'Route': 'terrassement',
  'Umbrella': 'amenagement_exterieur',
  'Droplet': 'amenagement_exterieur',
  'Flashlight': 'electricite',

  // Toiture
  'Warehouse': 'toiture_couverture',
  'CloudRain': 'toiture_couverture',
  'Sun': 'electricite',
  'LayoutGrid': 'toiture_couverture',

  // Spécialisé
  'Lock': 'menuiserie',
  'ArrowUpDown': 'gros_oeuvre',
  'Volume2': 'isolation',
  'Accessibility': 'renovation_complete',
  'ShieldCheck': 'electricite',
  'Wifi': 'electricite',
};

/**
 * Récupère le type de benchmark à partir du pictogramme du chantier
 */
export function getTypeBenchmarkFromPictogramme(pictogramme: string): keyof MargesParType {
  return MAPPING_PICTOGRAMME_TYPE[pictogramme] || 'renovation_complete';
}
